import { Fragment, useMemo, useState } from 'react';
import { ApiError } from '../lib/apiClient';
import {
  ALL_TIERS,
  groupByCategory,
  setFeatureTiers,
  tiersWithNoFeatures,
  type PlanTier,
  type ResolvedFeature,
} from '../lib/useFeatureCatalog';

const CATEGORY_LABEL: Record<string, string> = {
  device: 'Device',
  ai: 'AI',
  safety: 'Safety',
  data: 'Data',
  care: 'Care',
};

/**
 * Which tiers include which capability.
 *
 * Rows come from the backend's FEATURE_MANIFEST, so a capability engineering
 * ships appears here with no change to this file — that is the point of the
 * catalog and the reason nothing is hardcoded below.
 */
export function FeatureMatrix({
  features,
  onChanged,
  loaded = true,
}: {
  features: ResolvedFeature[];
  onChanged: () => void;
  /**
   * Whether `features` reflects a successful catalog load, as opposed to
   * Plans.tsx's still-loading or errored `[]`. Defaults to `true` so callers
   * that already know they have good data (and every existing test) need not
   * pass it. `false` suppresses `tiersWithNoFeatures` — an empty `features`
   * array from a failed fetch is not the same fact as a tier genuinely
   * configured with zero features, and must not raise the same alarm.
   */
  loaded?: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const groups = useMemo(() => groupByCategory(features), [features]);
  const emptyTiers = useMemo(
    () => (loaded ? tiersWithNoFeatures(features) : []),
    [features, loaded],
  );

  const toggle = async (f: ResolvedFeature, tier: PlanTier) => {
    const on = f.tiers.includes(tier);
    if (on && f.safetyCritical) return;
    const next = on ? f.tiers.filter((t) => t !== tier) : [...f.tiers, tier];
    const key = `${f.id}:${tier}`;
    setBusy(key);
    setError(null);
    try {
      await setFeatureTiers(f.id, next);
      onChanged();
    } catch (err) {
      // A 403 here is the safety-critical rule refusing the change, which is a
      // meaningful answer rather than a fault — surface the server's wording.
      setError(err instanceof ApiError ? err.message : `Failed to update ${f.nameEn}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      {error && <div className="error-banner" role="alert">{error}</div>}

      {emptyTiers.length > 0 && (
        <div className="warning-banner" role="alert">
          No features are assigned to <strong>{emptyTiers.join(', ')}</strong>. That tier
          will render an empty paywall.
        </div>
      )}

      <div className="dt-matrix">
      <div className="table-wrap">
        <table className="data-table dt-table">
          <thead>
            <tr>
              <th>Feature</th>
              {ALL_TIERS.map((t) => (
                <th key={t} style={{ textTransform: 'capitalize' }}>{t}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map(([category, rows]) => (
              <Fragment key={category}>
                <tr>
                  <td colSpan={ALL_TIERS.length + 1} className="header-section-label">
                    {CATEGORY_LABEL[category] ?? category}
                  </td>
                </tr>
                {rows.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <div>{f.nameEn}</div>
                      <div className="cell-sub">
                        {f.descriptionEn}
                        {f.safetyCritical && (
                          <> · <strong>safety-critical</strong> — can be added to a tier, never removed</>
                        )}
                        {f.isOverridden && <> · overridden</>}
                      </div>
                    </td>
                    {ALL_TIERS.map((tier) => {
                      const on = f.tiers.includes(tier);
                      const locked = on && Boolean(f.safetyCritical);
                      const key = `${f.id}:${tier}`;
                      return (
                        <td key={tier}>
                          <button
                            type="button"
                            className={on ? 'btn btn-xs btn-primary' : 'btn btn-xs'}
                            aria-pressed={on}
                            aria-label={`${f.nameEn} — ${tier}`}
                            disabled={locked || busy === key}
                            onClick={() => void toggle(f, tier)}
                          >
                            {busy === key ? '…' : on ? 'On' : 'Off'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

export default FeatureMatrix;
